import { LanguagePicker } from '@/components/language-picker';
import { getFooterItems } from '@/constants/footer';
import { useLanguagePickerVisibility } from '@/hooks/use-language-picker-visibility';
import { useUrl } from '@/hooks/use-url';
import { useLocale, useTranslations } from 'next-intl';
import Image from 'next/image';
import Link from 'next/link';

type Props = {
  site: any;
  page?: any;
  hide_menu_by_index?: number[];
  footer_legals_brands?: string;
};

export default function Footer({ site, page, hide_menu_by_index, footer_legals_brands }: Props) {
  if (!site) return <></>;

  const locale = useLocale();
  const t = useTranslations('Footer');
  const { getAppUrl } = useUrl();

  let footerItems = getFooterItems(locale as string);

  // Filtrer les menus en fonction des index à masquer
  if (hide_menu_by_index && Array.isArray(hide_menu_by_index)) {
    footerItems = footerItems.filter((_, index) => !hide_menu_by_index.includes(index));
  }

  const getCurrentPath = () => {
    return page?.path || '/';
  };

  const showLanguagePicker = useLanguagePickerVisibility(getCurrentPath());

  const normalizeInternalLink = (url: string) => {
    if (url.startsWith('http')) {
      return url;
    }

    if (url.startsWith('/lp/')) {
      return url.replace('/lp/', '/');
    }

    if (!url.startsWith('/')) {
      return `/${url}`;
    }

    return url;
  };

  return (
    <footer id="bottom" data-template={page ? page.template : undefined}>
      <div className="inner relative">
        <div className="logo">
          <div className="flex items-start justify-between">
            <Image src="/images/logo/makemycv.svg" alt="MakeMyCV" width={139} height={26} loading="lazy" />
            {showLanguagePicker ? (
              <div className="block lg:hidden">
                <LanguagePicker size="xs" />
              </div>
            ) : null}
          </div>
          <p>{site.footer_legals}</p>
          {footer_legals_brands ? <p>{footer_legals_brands}</p> : null}
        </div>

        <div className="menus">
          {footerItems.map(
            (menu: { titleKey: string; type: string; children: { titleKey: string; url: string }[] }) => {
              return (
                <div className="menu" key={menu.titleKey}>
                  <p className="title">{t(menu.titleKey)}</p>
                  <ul>
                    {menu.children.map((link: { titleKey: string; url: string }) => {
                      if (link.url) {
                        if (link.url.indexOf('/create') > -1) {
                          return (
                            <li key={link.titleKey}>
                              <Link
                                href={getAppUrl('create/' + link.url.split('/create/')[1]) + '?ref=' + getCurrentPath()}
                              >
                                <span className="link">{t(link.titleKey)}</span>
                              </Link>
                            </li>
                          );
                        } else if (link.url.includes('app.makemycv')) {
                          const urlParts = link.url.split('/');
                          const path = urlParts.slice(3).join('/');
                          return (
                            <li key={link.titleKey}>
                              <Link href={getAppUrl(path)}>
                                <span className="link">{t(link.titleKey)}</span>
                              </Link>
                            </li>
                          );
                        } else {
                          return (
                            <li key={link.titleKey}>
                              <Link href={normalizeInternalLink(link.url)}>{t(link.titleKey)}</Link>
                            </li>
                          );
                        }
                      } else {
                        return <li key={link.titleKey}>{t(link.titleKey)}</li>;
                      }
                    })}
                  </ul>
                </div>
              );
            }
          )}
        </div>
        {showLanguagePicker ? (
          <div className="hidden lg:block lg:min-w-28">
            <LanguagePicker />
          </div>
        ) : null}
      </div>
    </footer>
  );
}
